import { useState, useEffect } from 'react';
import { Card, Alert, Button } from 'react-bootstrap';
import AppScriptAPI from '../../services/appscriptAPI';
import AppToast from '../../components/AppToast';
import { useUserPermissions } from '../../contexts/UserPermissionsContext';
import { filterUsersByAdminScope } from '../../utils/accessControl';
import { useConfirmModal } from '../../hooks/useConfirmModal';
import UserStatCards from './components/UserStatCards';
import UserTabBar from './components/UserTabBar';
import UserTable from './components/UserTable';
import PermissionsModal from './components/PermissionsModal';
import './UserManagement.css';

function UserManagement() {
  const [activeTab, setActiveTab] = useState('pending');
  const [users, setUsers] = useState({ pending: [], active: [], suspended: [] });
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [toast, setToast]         = useState(null);

  // Modal state
  const [showModal, setShowModal]       = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving]             = useState(false);

  // Location data
  const [locationData, setLocationData]       = useState({ provinces: [], districts: {}, divisions: {} });
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [selectedProvinces, setSelectedProvinces] = useState([]);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [selectedDivisions, setSelectedDivisions] = useState([]);

  const { userPermissions } = useUserPermissions();
  const { confirm, ConfirmModalJSX } = useConfirmModal();

  useEffect(() => { loadUsers(); loadLocationData(); }, []);

  // ── Toast helper ────────────────────────────────────────────────────────────
  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Data loading ────────────────────────────────────────────────────────────
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await AppScriptAPI.getAllUsers();
      if (!response.success) throw new Error(response.message || 'Failed to load users');
      const all = response.users;
      setUsers({
        pending:   all.filter(u => u.status === 'pending'), // all pending visible to any admin
        active:    filterUsersByAdminScope(all.filter(u => u.status === 'active'), userPermissions),
        suspended: filterUsersByAdminScope(all.filter(u => u.status === 'suspended'), userPermissions),
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadLocationData = async () => {
    try {
      setLoadingLocations(true);
      const response = await AppScriptAPI.getLocationDataForAnalytics();
      setLocationData({
        provinces: response.provinces || [],
        districts: response.districts || {},
        divisions: response.divisions || {},
      });
    } catch {
      // silent — location data is only needed for the modal
    } finally {
      setLoadingLocations(false);
    }
  };

  // ── Modal ───────────────────────────────────────────────────────────────────
  const openModal = (user) => {
    setSelectedUser({
      ...user,
      access_level: user.access_level || 'dsd',
      access_type:  user.access_type  || 'viewer',
    });
    const splitCodes = val => val ? String(val).split(',').map(s => s.trim()).filter(Boolean) : [];
    setSelectedProvinces(splitCodes(user.allowed_provinces));
    setSelectedDistricts(splitCodes(user.allowed_districts));
    setSelectedDivisions(splitCodes(user.allowed_dsds));
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setSelectedUser(null); };

  // ── Actions ─────────────────────────────────────────────────────────────────
  const handleSavePermissions = async () => {
    try {
      setSaving(true);
      let allowedProvinces = '', allowedDistricts = '', allowedDsds = '';
      if (selectedUser.access_level === 'province')  allowedProvinces = selectedProvinces.join(',');
      if (selectedUser.access_level === 'district')  allowedDistricts = selectedDistricts.join(',');
      if (selectedUser.access_level === 'dsd')       allowedDsds      = selectedDivisions.join(',');

      const response = await AppScriptAPI.updateUserPermissions({
        email:             selectedUser.email,
        status:            'active',
        access_level:      selectedUser.access_level,
        access_type:       selectedUser.access_type,
        allowed_provinces: allowedProvinces,
        allowed_districts: allowedDistricts,
        allowed_dsds:      allowedDsds,
      });
      if (!response.success) throw new Error(response.message || 'Failed to update permissions');
      closeModal();
      await loadUsers();
      showToast('success', 'User permissions updated successfully.');
    } catch (e) {
      showToast('danger', 'Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSuspend = async (email) => {
    if (!(await confirm('Are you sure you want to suspend this user?'))) return;
    try {
      const response = await AppScriptAPI.updateUserStatus(email, 'suspended');
      if (!response.success) throw new Error(response.message || 'Failed to suspend user');
      await loadUsers();
      showToast('success', 'User suspended.');
    } catch (e) {
      showToast('danger', 'Error: ' + e.message);
    }
  };

  const handleActivate = async (email) => {
    try {
      const response = await AppScriptAPI.updateUserStatus(email, 'active');
      if (!response.success) throw new Error(response.message || 'Failed to activate user');
      await loadUsers();
      showToast('success', 'User activated.');
    } catch (e) {
      showToast('danger', 'Error: ' + e.message);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div>
      {ConfirmModalJSX}

      {/* Toast notification */}
      <AppToast show={!!toast} type={toast?.type} message={toast?.msg} onClose={() => setToast(null)} />

      {/* Page header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 mb-0">User Management</h1>
          <small className="text-muted">Manage system users, roles, and access permissions</small>
        </div>
        <Button variant="outline-primary" size="sm" onClick={loadUsers} disabled={loading}>
          <i className="bi bi-arrow-clockwise me-1"></i>Refresh
        </Button>
      </div>

      {/* Stat cards */}
      <UserStatCards users={users} loading={loading} />

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
          <i className="bi bi-exclamation-triangle me-2"></i>{error}
        </Alert>
      )}

      {/* Tab card */}
      <Card className="border-0 um-main-card">
        <UserTabBar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          counts={{ pending: users.pending.length, active: users.active.length, suspended: users.suspended.length }}
        />
        <Card.Body className="pt-3">
          <UserTable
            users={users[activeTab]}
            status={activeTab}
            loading={loading}
            onApprove={openModal}
            onSuspend={handleSuspend}
            onActivate={handleActivate}
            locationData={locationData}
            currentUserEmail={userPermissions?.email}
          />
        </Card.Body>
      </Card>

      {/* Permissions / approval modal */}
      <PermissionsModal
        show={showModal}
        onClose={closeModal}
        onSave={handleSavePermissions}
        saving={saving}
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        locationData={locationData}
        loadingLocations={loadingLocations}
        selectedProvinces={selectedProvinces}
        setSelectedProvinces={setSelectedProvinces}
        selectedDistricts={selectedDistricts}
        setSelectedDistricts={setSelectedDistricts}
        selectedDivisions={selectedDivisions}
        setSelectedDivisions={setSelectedDivisions}
      />
    </div>
  );
}

export default UserManagement;
